import Fade from "embla-carousel-fade";
import Image from "next/image";
import { Avatar, AvatarImage } from "components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem } from "components/ui/carousel";
import { ImageSliderProps } from "types/tiles";
import ImageSliderButtons from "./ImageSliderButtons";

const ImageSlider = ({ images, bgColor }: { images: ImageSliderProps[]; bgColor?: string }) => {
  return (
    <div className="z-[10] w-full !overflow-visible rounded-lg object-cover @3xl:rounded-2xl">
      <Carousel plugins={[Fade()]}>
        <CarouselContent className="flex items-center">
          {images?.map((image, index) => (
            <CarouselItem key={`${index}${image.src ? `_${image.src.split("/").pop()}` : ""}`}>
              {image.top && (
                <div className="absolute right-4 top-4 z-20 rounded bg-pink-format px-3 py-[7px] text-xs uppercase !leading-[18px] tracking-[0.06em] text-purple-700 @3xl:right-8 @3xl:top-8">
                  {image.top}
                </div>
              )}
              <div
                className="group relative h-[450px] w-full overflow-hidden rounded-2xl"
                style={{ backgroundColor: bgColor }}
              >
                <Image
                  alt={image.alt || "template_image"}
                  sizes="(max-width: 639px) 100vw, (max-width: 1535px) 80vw, 50vw"
                  src={image.src}
                  decoding="async"
                  quality={75}
                  className="object-cover blur-0 grayscale-0 transition duration-500 ease-in-out hover:scale-105 group-hover:scale-105"
                  fill
                  loading={index === 0 ? "eager" : "lazy"}
                  priority={index === 0} // Set priority to true for the first image
                />
                <div className="flex w-full items-center justify-between whitespace-nowrap @3xl:w-auto @3xl:justify-start">
                  <ImageSliderButtons />
                </div>
              </div>
              <div
                className="z-30 flex h-[250px] w-full flex-col items-center justify-between  py-12 text-center"
                style={{ backgroundColor: bgColor }}
              >
                <div className="flex w-full flex-col justify-between pb-28 @3xl:mb-0 @3xl:w-auto @3xl:justify-start">
                  <p className="mb-6 line-clamp-1 cursor-default text-center text-[14px] italic leading-[24px] tracking-[.01em] text-primary-700 @3xl:flex-row @3xl:items-center">
                    {image.subtitle}
                  </p>
                  {image.review && (
                    <div>
                      <div className="mb-4 px-6">
                        <span className="text-primary-600">&quot;</span>
                        {image.review.body}
                        <span className="text-primary-600">&quot;</span>
                      </div>

                      {image.review.client_name && (
                        <div className="mt-2 flex items-center justify-center space-x-1 text-base">
                          <Avatar className="bg-background  h-6 w-6 border">
                            <AvatarImage asChild src={image.review.fullAvatarUrl}>
                              {image.review.fullAvatarUrl && (
                                <Image
                                  src={image.review.fullAvatarUrl}
                                  alt="SoldAve Happy Review"
                                  width={24}
                                  height={24}
                                  style={{
                                    objectFit: "cover"
                                  }}
                                />
                              )}
                            </AvatarImage>
                          </Avatar>
                          <div className="text-sm font-medium !leading-none text-primary-700">
                            {image.review.client_name || ""}
                          </div>
                          <svg
                            enableBackground="new 0 0 1991.3 2143.2"
                            viewBox="0 0 1991.3 2143.2"
                            xmlns="http://www.w3.org/2000/svg"
                            height="18px" // Set the height to 20px
                          >
                            <path
                              d="m1851.6 1735.6c-15 111.6-90.1 208.1-195.2 251-51.5 21.4-107.3 27.9-163.1 21.4-53.6-6.4-107.3-23.6-163-55.7-77.2-43-154.5-109.4-244.6-208.1 141.6-173.8 227.4-332.5 259.6-474.1 15-66.5 17.2-126.6 10.7-182.4-8.6-53.6-27.9-103-57.9-145.9-66.5-96.5-178.1-152.3-302.5-152.3s-236 57.9-302.5 152.3c-30 42.9-49.3 92.3-57.9 145.9-8.6 55.8-6.4 118 10.7 182.4 32.2 141.6 120.1 302.5 259.6 476.2-88 98.7-167.3 165.2-244.6 208.1-55.8 32.2-109.4 49.4-163 55.8-55.3 6.2-111.2-1.2-163-21.4-105.1-42.9-180.2-139.5-195.2-251-6.4-53.6-2.1-107.2 19.3-167.3 6.4-21.5 17.2-42.9 27.9-68.6 15-34.3 32.2-70.8 49.3-107.3l2.2-4.3c148-319.7 306.8-645.8 472-963.3l6.4-12.9c17.2-32.1 34.3-66.5 51.5-98.7 17.2-34.3 36.5-66.5 60.1-94.4 45.1-51.5 105.1-79.4 171.6-79.4s126.6 27.9 171.6 79.4c23.6 27.9 42.9 60.1 60.1 94.4 17.2 32.2 34.3 66.5 51.5 98.6l6.5 12.9c163 319.6 321.8 645.7 469.8 965.4v2.1c17.2 34.3 32.2 73 49.3 107.3 10.7 25.8 21.5 47.2 27.9 68.6 17.1 55.9 23.5 109.5 14.9 165.3zm-856-100.9c-115.8-145.9-190.9-283.2-216.7-399-10.7-49.4-12.9-92.3-6.4-130.9 4.3-34.3 17.2-64.4 34.3-90.1 40.8-57.9 109.4-94.4 188.8-94.4s150.2 34.4 188.8 94.4c17.2 25.8 30 55.8 34.3 90.1 6.4 38.6 4.3 83.7-6.4 130.9-25.7 113.7-100.8 251-216.7 399zm967.6-111.5c-10.7-25.7-21.5-53.6-32.2-77.2-17.2-38.6-34.3-75.1-49.4-109.4l-2.1-2.1c-148-321.8-306.8-647.9-474.1-969.7l-6.4-12.9c-17.2-32.2-34.3-66.5-51.5-100.8-21.5-38.6-42.9-79.4-77.2-118-68.7-85.9-167.4-133.1-272.5-133.1-107.3 0-203.8 47.2-274.7 128.7-32.2 38.6-55.8 79.4-77.2 118-17.2 34.3-34.3 68.6-51.5 100.8l-6.4 12.8c-165.2 321.8-326.1 647.9-474.1 969.7l-2.1 4.3c-15 34.3-32.2 70.8-49.4 109.4-11.5 25.4-22.2 51.2-32.2 77.2-27.9 79.4-36.5 154.5-25.8 231.7 23.6 160.9 130.9 296.1 278.9 356.1 55.8 23.6 113.7 34.3 173.8 34.3 17.2 0 38.6-2.1 55.8-4.3 70.8-8.6 143.7-32.1 214.5-72.9 88-49.3 171.6-120.1 266-223.1 94.4 103 180.2 173.8 266 223.1 70.8 40.8 143.7 64.3 214.5 72.9 17.2 2.2 38.6 4.3 55.8 4.3 60.1 0 120.1-10.7 173.8-34.3 150.2-60.1 255.3-197.4 278.9-356.1 17.2-75 8.6-150-19.2-229.4z"
                              fill="#e0565b"
                            />
                          </svg>
                          <div className="text-sm  font-normal !leading-none text-primary-600">{image.review.date}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default ImageSlider;
